import { z } from 'zod';
import { IsoDateTimeSchema, SeveritySchema, UuidSchema } from './common.js';

export const CompetitorRunStatusSchema = z.enum([
  'queued',
  'sitemap_fetching',
  'classifying',
  'rank_checking',
  'backlink_checking',
  'digesting',
  'completed',
  'failed',
  'cancelled',
]);
export type CompetitorRunStatus = z.infer<typeof CompetitorRunStatusSchema>;

export const CompetitorSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  domain: z.string(),
  label: z.string().nullable(),
  created_at: IsoDateTimeSchema,
});
export type Competitor = z.infer<typeof CompetitorSchema>;

/**
 * Normalize a domain-like input into a bare hostname.
 * Accepts URLs (`https://www.example.com/path`) and bare domains (`example.com`).
 * Strips protocol, leading `www.`, port, path, query, fragment, trailing slash.
 * Lowercased + trimmed. Used by the server schema below so the API is forgiving.
 */
export function normalizeDomainInput(raw: string): string {
  let s = raw.trim().toLowerCase();
  s = s.replace(/^https?:\/\//, '');
  s = s.replace(/^www\./, '');
  s = s.split('/')[0] ?? s;
  s = s.split('?')[0] ?? s;
  s = s.split('#')[0] ?? s;
  s = s.split(':')[0] ?? s;
  return s;
}

const BARE_DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

export const CreateCompetitorRequestSchema = z.object({
  project_id: UuidSchema,
  domain: z
    .string()
    .trim()
    .min(3, 'Enter a domain')
    .max(253, 'Domain is too long')
    .transform(normalizeDomainInput)
    .refine((v) => BARE_DOMAIN_RE.test(v), {
      message: 'Must look like a domain — e.g. moz.com (no protocol, no path)',
    }),
  label: z.string().trim().max(120).optional(),
});
export type CreateCompetitorRequest = z.infer<typeof CreateCompetitorRequestSchema>;

export const CreateCompetitorRunRequestSchema = z.object({
  project_id: UuidSchema,
  competitor_ids: z.array(UuidSchema).min(1).max(10),
});
export type CreateCompetitorRunRequest = z.infer<typeof CreateCompetitorRunRequestSchema>;

export const CompetitorRunSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  status: CompetitorRunStatusSchema,
  competitor_ids: z.array(UuidSchema),
  digest_md: z.string().nullable(),
  started_at: IsoDateTimeSchema.nullable(),
  completed_at: IsoDateTimeSchema.nullable(),
  failed_at: IsoDateTimeSchema.nullable(),
  failure_reason: z.string().nullable(),
  created_at: IsoDateTimeSchema,
});
export type CompetitorRun = z.infer<typeof CompetitorRunSchema>;

export const CompetitorFindingSchema = z.object({
  id: UuidSchema,
  competitor_run_id: UuidSchema,
  competitor_id: UuidSchema.nullable(),
  type: z.string(),
  severity: SeveritySchema,
  title: z.string(),
  detail: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  detected_at: IsoDateTimeSchema,
});
export type CompetitorFinding = z.infer<typeof CompetitorFindingSchema>;

/** LLM output for competitor digest narrative. */
export const CompetitorDigestOutputSchema = z.object({
  summary: z.string().min(20).max(2000),
  recommendations: z.array(z.string().min(5).max(280)).min(1).max(5),
});
export type CompetitorDigestOutput = z.infer<typeof CompetitorDigestOutputSchema>;

/** LLM output for classifying new competitor pages by topical bucket. */
export const PageClassificationOutputSchema = z.object({
  classifications: z.array(
    z.object({
      url: z.string(),
      bucket: z.enum(['blog', 'product', 'landing', 'docs', 'other']),
    }),
  ),
});
export type PageClassificationOutput = z.infer<typeof PageClassificationOutputSchema>;
